// Eskom PARCC — Report to Management composer (Phase 2 in-app lane).
//
// Edit + persist + download ONE division × quarter Report to Management. The
// auto-draft is computed CLIENT-SIDE from the live register (buildRtmDocModel);
// admin-api persists + scopes (RtmReport / RtmFinding, 404 on a division miss).
// The composer holds the editable fields in local state seeded from the persisted
// row; Save patches; Download re-renders the merged model with the existing DOCX /
// PDF renderers. Issue (formal, numbered) is the next slice.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Banner, LoadingState, ErrorState, useToast } from '../../../components/ui';
import { useAuth } from '../../../lib/auth';
import { useTransactions, useFindings } from '../../../hooks';
import { canAuthorRtm } from '../../../lib/permissions';
import { canonicalDivision } from '../../../domain/divisions';
import { effectiveComplianceByRef } from '../../transactions/compliance';
import { formatRand } from '../../../lib/money';
import {
  adminApi,
  type RtmReportRow,
  type RtmFindingRowRead,
} from '../../../lib/adminApi';
import { deckDivisionLabel } from '../reportScope';
import { buildRtmDraftInput } from './rtmDraftInput';
import { buildRtmDocModel, type RtmDocModel, type RtmTable1Row } from './buildRtmDocModel';
import {
  reportFieldsFromModel,
  findingRowsFromModel,
  mergeModel,
  type RtmReportFields,
  type RtmFindingRow,
} from './rtmPersist';

/** ISO (or yyyy-mm-dd) → "11 September 2026"; falls back to the input. */
function dateLong(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Seed the editable report fields from a persisted row. */
function fieldsFromRow(r: RtmReportRow): RtmReportFields {
  return {
    title: r.title ?? '',
    reportDate: r.reportDate ?? '',
    signatoryName: r.signatoryName ?? '',
    signatoryTitle: r.signatoryTitle ?? '',
    addresseesJson: (r.addresseesJson as RtmDocModel['addressees']) ?? [],
    acknowledgersJson: (r.acknowledgersJson as RtmDocModel['acknowledgers']) ?? [],
    coverLetterJson: (r.coverLetterJson as string[]) ?? [],
    keyRecommendationsJson: (r.keyRecommendationsJson as string[]) ?? [],
    populationInitial: r.populationInitial ?? 0,
    populationFinal: r.populationFinal ?? 0,
    populationNote: r.populationNote ?? '',
    classOverridesJson: (r.classOverridesJson as Record<string, string>) ?? null,
    table1OverridesJson: (r.table1OverridesJson as Record<string, Partial<RtmTable1Row>>) ?? null,
    overallRating: r.overallRating,
    conclusionText: r.conclusionText ?? '',
    managementResponse: r.managementResponse ?? '',
    unclosedCountOverride: r.unclosedCountOverride,
    ieCountOverride: r.ieCountOverride,
    ieValueOverride: r.ieValueOverride,
  };
}

/** An editable finding row in composer state. */
interface EditFinding {
  id: string;
  seq: number;
  number: string;
  heading: string;
  findingTitle: string;
  rating: number;
  criteria: string;
  observation: string;
  risk: string;
  rootCausePrimary: string;
  rootCauseSecondary: string;
  recommendations: string; // newline-joined for the textarea
  linkedRefs: string[];
  autoDraftJson: unknown;
}

function editFromRow(r: RtmFindingRowRead): EditFinding {
  return {
    id: r.id,
    seq: r.seq,
    number: r.number ?? '',
    heading: r.heading ?? '',
    findingTitle: r.findingTitle ?? '',
    rating: r.rating ?? 0,
    criteria: r.criteria ?? '',
    observation: r.observation ?? '',
    risk: r.risk ?? '',
    rootCausePrimary: r.rootCausePrimary ?? '',
    rootCauseSecondary: r.rootCauseSecondary ?? '',
    recommendations: ((r.recommendationsJson as string[]) ?? []).join('\n'),
    linkedRefs: (r.linkedRefsJson as string[]) ?? [],
    autoDraftJson: r.autoDraftJson,
  };
}

const RATING_LABEL: Record<number, string> = { 1: '1 — Housekeeping', 2: '2 — Policy & Procedures', 3: '3 — PFMA / Potential IE', 4: '4 — Significant' };

/** Blob → base64 (chunked, to avoid a call-stack overflow on large docs). */
async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 };
const areaStyle: React.CSSProperties = { width: '100%', minHeight: 72, font: 'inherit', padding: 8, boxSizing: 'border-box' };
const inputStyle: React.CSSProperties = { font: 'inherit', padding: '6px 8px', boxSizing: 'border-box' };

export function RtmComposerPage(): React.JSX.Element {
  const params = useParams<{ fy: string; quarter: string; division: string }>();
  const fy = params.fy ?? '';
  const quarter = params.quarter ?? '';
  const division = canonicalDivision(decodeURIComponent(params.division ?? ''));
  const navigate = useNavigate();
  const { show } = useToast();
  const { grant } = useAuth();

  const transactionsQuery = useTransactions();
  const findingsQuery = useFindings();
  const visible = useMemo(() => transactionsQuery.data ?? [], [transactionsQuery.data]);
  const findings = useMemo(() => findingsQuery.data ?? [], [findingsQuery.data]);
  const effLevel = useMemo(() => effectiveComplianceByRef(visible, findings), [visible, findings]);

  /** The client-side auto-draft for this division × quarter (the render base). */
  const autoDraft = useMemo<RtmDocModel>(
    () =>
      buildRtmDocModel(
        buildRtmDraftInput(visible, findings, effLevel, {
          fy,
          quarter,
          division,
          reportDate: dateLong(new Date().toISOString()),
        }),
      ),
    [visible, findings, effLevel, fy, quarter, division],
  );

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [report, setReport] = useState<RtmReportRow | null>(null);
  const [form, setForm] = useState<RtmReportFields | null>(null);
  const [editFindings, setEditFindings] = useState<EditFinding[]>([]);
  const [busy, setBusy] = useState<null | 'create' | 'save' | 'docx' | 'pdf' | 'issue'>(null);

  const reportKey = `${fy}|${quarter}|${division}`;
  const canAuthor = canAuthorRtm(grant, division);

  const seedFrom = useCallback((r: RtmReportRow, f: RtmFindingRowRead[]) => {
    setReport(r);
    setForm(fieldsFromRow(r));
    setEditFindings(f.map(editFromRow).sort((a, b) => a.seq - b.seq));
  }, []);

  useEffect(() => {
    if (!canAuthor) {
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    adminApi
      .rtmReport(reportKey)
      .then((res) => {
        if (!live) return;
        seedFrom(res.report, res.findings);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (!live) return;
        // 404 = no report yet (offer to create); other errors surface.
        const msg = err instanceof Error ? err.message : String(err);
        if (/not found/i.test(msg)) {
          setReport(null);
        } else {
          setLoadError(msg);
        }
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [reportKey, canAuthor, seedFrom]);

  async function createReport(): Promise<void> {
    setBusy('create');
    try {
      const res = await adminApi.rtmCreateReport(
        fy,
        quarter,
        division,
        reportFieldsFromModel(autoDraft),
        findingRowsFromModel(autoDraft),
      );
      seedFrom(res.report, res.findings);
      show('Report drafted from the register — edit and save.', 'success');
    } catch (err) {
      show(err instanceof Error ? err.message : 'Could not create the report.', 'error');
    } finally {
      setBusy(null);
    }
  }

  const patch = <K extends keyof RtmReportFields>(k: K, v: RtmReportFields[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));
  const patchFinding = (id: string, k: keyof EditFinding, v: unknown) =>
    setEditFindings((fs) => fs.map((f) => (f.id === id ? { ...f, [k]: v } : f)));

  /** Persist the current report + finding edits (no reload/toast) — shared by save + issue. */
  async function persistEdits(): Promise<void> {
    if (!form) return;
    await adminApi.rtmPatchReport(reportKey, form);
    for (const f of editFindings) {
      await adminApi.rtmPatchFinding(f.id, {
        seq: f.seq,
        number: f.number,
        heading: f.heading,
        findingTitle: f.findingTitle,
        rating: f.rating,
        criteria: f.criteria,
        observation: f.observation,
        risk: f.risk,
        rootCausePrimary: f.rootCausePrimary,
        rootCauseSecondary: f.rootCauseSecondary,
        recommendationsJson: f.recommendations.split('\n').map((s) => s.trim()).filter(Boolean),
        linkedRefsJson: f.linkedRefs,
      });
    }
  }

  async function saveAll(): Promise<void> {
    if (!form || !report) return;
    setBusy('save');
    try {
      await persistEdits();
      const res = await adminApi.rtmReport(reportKey);
      seedFrom(res.report, res.findings);
      show('Report saved.', 'success');
    } catch (err) {
      show(err instanceof Error ? err.message : 'Could not save the report.', 'error');
    } finally {
      setBusy(null);
    }
  }

  async function issue(): Promise<void> {
    if (!form || !report) return;
    if (!window.confirm(`Issue the Report to Management for ${deckDivisionLabel(division)} ${fy} ${quarter}? It will be numbered, stored, and become read-only.`)) return;
    setBusy('issue');
    try {
      await persistEdits(); // the issued document reflects exactly what is persisted
      const model = mergedModel();
      const { generateRtmDocxBlob, generateRtmPdfBlob } = await import('./download');
      const [docx, pdf] = await Promise.all([generateRtmDocxBlob(model), generateRtmPdfBlob(model)]);
      const [docxBase64, pdfBase64] = await Promise.all([blobToBase64(docx), blobToBase64(pdf)]);
      const res = await adminApi.rtmIssue(reportKey, {
        docxBase64,
        pdfBase64,
        model: model as unknown,
        asOfDate: new Date().toISOString().slice(0, 10),
      });
      const reload = await adminApi.rtmReport(reportKey);
      seedFrom(reload.report, reload.findings);
      show(`Issued as ${res.reportNumber}.`, 'success');
    } catch (err) {
      show(err instanceof Error ? err.message : 'Could not issue the report.', 'error');
    } finally {
      setBusy(null);
    }
  }

  /** Merge the current edits onto the auto-draft base for rendering. */
  function mergedModel(): RtmDocModel {
    if (!form) return autoDraft;
    const rows: RtmFindingRow[] = editFindings.map((f) => ({
      seq: f.seq,
      number: f.number,
      heading: f.heading,
      classification: '',
      findingTitle: f.findingTitle,
      rating: f.rating,
      tier: (f.autoDraftJson as { tier?: string } | null)?.tier ?? '',
      criteria: f.criteria,
      observation: f.observation,
      risk: f.risk,
      summaryObservation: (f.autoDraftJson as { summaryObservation?: string } | null)?.summaryObservation ?? '',
      rootCausePrimary: f.rootCausePrimary,
      rootCauseSecondary: f.rootCauseSecondary,
      rootCauseText: '',
      recommendationsJson: f.recommendations.split('\n').map((s) => s.trim()).filter(Boolean),
      linkedRefsJson: f.linkedRefs,
      linkedCriteriaJson: [],
      subObservationsJson: [],
      autoDraftJson: (f.autoDraftJson as RtmFindingRow['autoDraftJson']) ?? (autoDraft.findings[0] ?? null),
    }));
    return mergeModel(autoDraft, form, rows);
  }

  async function download(kind: 'docx' | 'pdf'): Promise<void> {
    setBusy(kind);
    try {
      const model = mergedModel();
      const { saveBlob, generateRtmDocxBlob, generateRtmPdfBlob } = await import('./download');
      const stem = `Report to Management - ${deckDivisionLabel(division)} - ${fy} ${quarter}`.replace(/[\\/:*?"<>|]+/g, ' ').trim();
      const blob = kind === 'docx' ? await generateRtmDocxBlob(model) : await generateRtmPdfBlob(model);
      saveBlob(blob, `${stem}.${kind}`);
      show(`Report (${kind.toUpperCase()}) downloaded.`, 'success');
    } catch (err) {
      show(err instanceof Error ? err.message : 'Could not render the report.', 'error');
    } finally {
      setBusy(null);
    }
  }

  if (!canAuthor) {
    return (
      <Card title="Report to Management">
        <Banner tone="bad">You are not authorised to author the Report to Management for {deckDivisionLabel(division) || 'this division'}.</Banner>
      </Card>
    );
  }
  if (transactionsQuery.isLoading || findingsQuery.isLoading || loading) return <LoadingState message="Loading the register and report…" />;
  if (loadError) return <ErrorState message={loadError} />;

  const model = mergedModel();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card
        title={`Report to Management — ${deckDivisionLabel(division)}`}
        subtitle={`${fy} ${quarter} · population ${autoDraft.populationInitial} · ${autoDraft.unclosedCount} unclosed · ${autoDraft.ieCount} potential IE (${formatRand(autoDraft.ieValue) || 'R 0'})`}
        actions={
          <div className="actions" style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={() => navigate('/reports')}>Back to Reports</Button>
            {report && (
              <>
                <Button variant="ghost" disabled={busy !== null} onClick={() => void download('docx')}>{busy === 'docx' ? 'Rendering…' : 'Download Word'}</Button>
                <Button variant="ghost" disabled={busy !== null} onClick={() => void download('pdf')}>{busy === 'pdf' ? 'Rendering…' : 'Download PDF'}</Button>
                {report.status !== 'issued' && (
                  <>
                    <Button disabled={busy !== null} onClick={() => void saveAll()}>{busy === 'save' ? 'Saving…' : 'Save'}</Button>
                    <Button variant="primary" disabled={busy !== null} onClick={() => void issue()}>{busy === 'issue' ? 'Issuing…' : 'Issue'}</Button>
                  </>
                )}
              </>
            )}
          </div>
        }
      >
        {report?.status === 'issued' && <Banner tone="info">This report has been issued ({report.issuedReportNumber}); it is read-only.</Banner>}
        {!report && (
          <div>
            <p className="small">No report has been drafted for {deckDivisionLabel(division)} · {fy} {quarter} yet. Draft one from the current register — you can then edit every section and download it.</p>
            <Button disabled={busy !== null} onClick={() => void createReport()}>{busy === 'create' ? 'Drafting…' : 'Draft from the register'}</Button>
          </div>
        )}
      </Card>

      {form && (
        <>
          <Card title="Header & cover letter">
            <label style={labelStyle}><span className="small">Title</span>
              <input style={inputStyle} value={form.title} onChange={(e) => patch('title', e.target.value)} /></label>
            <label style={labelStyle}><span className="small">Report date</span>
              <input style={inputStyle} value={form.reportDate} onChange={(e) => patch('reportDate', e.target.value)} /></label>
            <label style={labelStyle}><span className="small">Signatory</span>
              <input style={inputStyle} value={form.signatoryName} onChange={(e) => patch('signatoryName', e.target.value)} /></label>
            <label style={labelStyle}><span className="small">Signatory title</span>
              <input style={inputStyle} value={form.signatoryTitle} onChange={(e) => patch('signatoryTitle', e.target.value)} /></label>
            <label style={labelStyle}><span className="small">Cover letter (one paragraph per line)</span>
              <textarea style={{ ...areaStyle, minHeight: 120 }} value={form.coverLetterJson.join('\n\n')} onChange={(e) => patch('coverLetterJson', e.target.value.split(/\n\n+/).map((s) => s.trim()).filter(Boolean))} /></label>
          </Card>

          <Card title="Population & conclusion">
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <label style={{ ...labelStyle, flex: 1 }}><span className="small">Population (initial)</span>
                <input type="number" style={inputStyle} value={form.populationInitial} onChange={(e) => patch('populationInitial', Number(e.target.value) || 0)} /></label>
              <label style={{ ...labelStyle, flex: 1 }}><span className="small">Population (final)</span>
                <input type="number" style={inputStyle} value={form.populationFinal} onChange={(e) => patch('populationFinal', Number(e.target.value) || 0)} /></label>
              <label style={{ ...labelStyle, flex: 1 }}><span className="small">Overall rating (Annexure C)</span>
                <select style={inputStyle} value={form.overallRating ?? ''} onChange={(e) => patch('overallRating', e.target.value === '' ? null : Number(e.target.value))}>
                  <option value="">— choose —</option>
                  {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{RATING_LABEL[n]}</option>)}
                </select></label>
            </div>
            <label style={labelStyle}><span className="small">Population note</span>
              <input style={inputStyle} value={form.populationNote} onChange={(e) => patch('populationNote', e.target.value)} /></label>
            <label style={labelStyle}><span className="small">Conclusion</span>
              <textarea style={{ ...areaStyle, minHeight: 120 }} value={form.conclusionText} onChange={(e) => patch('conclusionText', e.target.value)} /></label>
            <label style={labelStyle}><span className="small">Key recommendations (one per line)</span>
              <textarea style={areaStyle} value={form.keyRecommendationsJson.join('\n')} onChange={(e) => patch('keyRecommendationsJson', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} /></label>
            <label style={labelStyle}><span className="small">Management response</span>
              <textarea style={areaStyle} value={form.managementResponse} onChange={(e) => patch('managementResponse', e.target.value)} /></label>
          </Card>

          <Card title={`Findings (${editFindings.length})`} subtitle="Auto-drafted from the outstanding transactions, grouped by tier. Edit each section; Save persists them.">
            {editFindings.length === 0 && <p className="small">No outstanding findings drafted — every reviewed transaction in scope is compliant, self-declared or addressed.</p>}
            {editFindings.map((f) => (
              <div key={f.id} style={{ borderTop: '1px solid var(--border, #ddd)', paddingTop: 12, marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <label style={{ ...labelStyle, flex: 2 }}><span className="small">{f.number} · Heading</span>
                    <input style={inputStyle} value={f.heading} onChange={(e) => patchFinding(f.id, 'heading', e.target.value)} /></label>
                  <label style={{ ...labelStyle, flex: 1 }}><span className="small">Rating</span>
                    <select style={inputStyle} value={f.rating} onChange={(e) => patchFinding(f.id, 'rating', Number(e.target.value))}>
                      {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{RATING_LABEL[n]}</option>)}
                    </select></label>
                </div>
                <label style={labelStyle}><span className="small">Finding title</span>
                  <input style={inputStyle} value={f.findingTitle} onChange={(e) => patchFinding(f.id, 'findingTitle', e.target.value)} /></label>
                <label style={labelStyle}><span className="small">Criteria</span>
                  <textarea style={areaStyle} value={f.criteria} onChange={(e) => patchFinding(f.id, 'criteria', e.target.value)} /></label>
                <label style={labelStyle}><span className="small">Observation</span>
                  <textarea style={{ ...areaStyle, minHeight: 100 }} value={f.observation} onChange={(e) => patchFinding(f.id, 'observation', e.target.value)} /></label>
                <label style={labelStyle}><span className="small">Risk</span>
                  <textarea style={areaStyle} value={f.risk} onChange={(e) => patchFinding(f.id, 'risk', e.target.value)} /></label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <label style={{ ...labelStyle, flex: 1 }}><span className="small">Root cause (primary)</span>
                    <input style={inputStyle} value={f.rootCausePrimary} onChange={(e) => patchFinding(f.id, 'rootCausePrimary', e.target.value)} /></label>
                  <label style={{ ...labelStyle, flex: 1 }}><span className="small">Root cause (secondary)</span>
                    <input style={inputStyle} value={f.rootCauseSecondary} onChange={(e) => patchFinding(f.id, 'rootCauseSecondary', e.target.value)} /></label>
                </div>
                <label style={labelStyle}><span className="small">Recommendations (one per line)</span>
                  <textarea style={areaStyle} value={f.recommendations} onChange={(e) => patchFinding(f.id, 'recommendations', e.target.value)} /></label>
                {f.linkedRefs.length > 0 && <p className="small">Linked: {f.linkedRefs.join(', ')}</p>}
              </div>
            ))}
          </Card>

          <Card title="Table 1 preview" subtitle="Gaps identified → addressed, by RTM class. Computed from the register; consultant overrides are the next slice.">
            <div style={{ overflowX: 'auto' }}>
              <table className="dz-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={{ textAlign: 'left' }}>RTM class</th><th>Identified</th><th>Addressed</th><th>Self-declared</th></tr></thead>
                <tbody>
                  {model.table1.map((r) => (
                    <tr key={r.rtmClass}><td>{r.rtmClass}</td><td style={{ textAlign: 'right' }}>{r.gapsIdentified}</td><td style={{ textAlign: 'right' }}>{r.gapsAddressed}</td><td style={{ textAlign: 'right' }}>{r.sdGaps}</td></tr>
                  ))}
                  <tr style={{ fontWeight: 600 }}><td>Total</td><td style={{ textAlign: 'right' }}>{model.table1Total.gapsIdentified}</td><td style={{ textAlign: 'right' }}>{model.table1Total.gapsAddressed}</td><td style={{ textAlign: 'right' }}>{model.table1Total.sdGaps}</td></tr>
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
